import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const Ctx = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [selection, setSelection] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tnt_selection") || "[]"); }
    catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tnt_wishlist") || "[]"); }
    catch { return []; }
  });
  const [compare, setCompare] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tnt_compare") || "[]"); }
    catch { return []; }
  });
  const [serviceSelection, setServiceSelection] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tnt_service_selection") || "[]"); }
    catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem("tnt_service_selection", JSON.stringify(serviceSelection));
  }, [serviceSelection]);
  const inServiceSelection = (id) => serviceSelection.some((s) => s.id === id);
  const toggleServiceSelection = (svc) => setServiceSelection((cur) =>
    cur.some((s) => s.id === svc.id) ? cur.filter((s) => s.id !== svc.id) : [...cur, { id: svc.id, name: svc.name, image: svc.photos?.[0] || svc.image_url || "" }]);
  const clearServiceSelection = () => setServiceSelection([]);

  useEffect(() => {
    const t = localStorage.getItem("tnt_token");
    if (!t) { setReady(true); return; }
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => localStorage.removeItem("tnt_token")).finally(() => setReady(true));
  }, []);

  useEffect(() => {
    localStorage.setItem("tnt_selection", JSON.stringify(selection));
  }, [selection]);
  useEffect(() => {
    localStorage.setItem("tnt_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);
  useEffect(() => {
    localStorage.setItem("tnt_compare", JSON.stringify(compare));
  }, [compare]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("tnt_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const signup = async (payload) => {
    const r = await api.post("/auth/signup", payload);
    localStorage.setItem("tnt_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const loginWithFirebase = async (idToken) => {
    const r = await api.post("/auth/firebase-phone", { id_token: idToken });
    localStorage.setItem("tnt_token", r.data.token);
    setUser(r.data.user);
    return { ...r.data.user, needs_name: !!r.data.needs_name };
  };
  const updateProfile = async (payload) => {
    const r = await api.patch("/auth/me", payload);
    setUser(r.data);
    return r.data;
  };
  const logout = () => {
    localStorage.removeItem("tnt_token");
    setUser(null);
  };

  const addToSelection = (product, quantity = 1, event_date = "", extras = {}) => {
    // extras: { price, custom_size: { width_ft, length_ft, sqft } }
    // When a size customizer or price override is supplied, treat this as a
    // distinct line item so a customer can add multiple sizes of the same
    // product without them collapsing into one row.
    const overridePrice = typeof extras.price === "number" ? extras.price : null;
    const customSize = extras.custom_size || null;
    setSelection((cur) => {
      const isCustom = overridePrice != null || customSize != null;
      const idx = isCustom ? -1 : cur.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const copy = [...cur];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...cur, {
        line_id: `${product.id}-${Date.now().toString(36)}`,
        product_id: product.id,
        product_name: product.name,
        price: overridePrice != null ? overridePrice : product.price,
        image: product.primary_image,
        colors: product.colors || [],
        quantity,
        event_date,
        custom_size: customSize,
        variation: extras.variation || null,
      }];
    });
  };
  const lineKey = (i) => i.line_id || i.product_id;
  const updateSelectionQty = (key, quantity) => {
    setSelection((cur) => cur.map((i) => lineKey(i) === key ? { ...i, quantity: Math.max(1, quantity) } : i));
  };
  const updateSelectionDate = (key, event_date) => {
    setSelection((cur) => cur.map((i) => lineKey(i) === key ? { ...i, event_date } : i));
  };
  const removeFromSelection = (key) => {
    setSelection((cur) => cur.filter((i) => lineKey(i) !== key && i.product_id !== key));
  };
  const clearSelection = () => setSelection([]);

  // Global quick-add popup: { productId, onAdded }
  const [quickAdd, setQuickAdd] = useState(null);
  const openQuickAdd = (product, onAdded) => setQuickAdd({ productId: typeof product === "string" ? product : product.id, onAdded });
  const closeQuickAdd = useCallback(() => setQuickAdd(null), []);

  const _mini = (p) => ({
    id: p.id, name: p.name, price: p.price,
    image: p.primary_image || p.image,
    main_category: p.main_category, sub_category: p.sub_category,
    included_items: p.included_items || [],
    description: p.description || "",
    colors: p.colors || [],
  });
  const isWishlisted = (id) => wishlist.some((i) => i.id === id);
  const toggleWishlist = (product) => {
    setWishlist((cur) =>
      cur.some((i) => i.id === product.id)
        ? cur.filter((i) => i.id !== product.id)
        : [...cur, _mini(product)]
    );
  };
  const clearWishlist = () => setWishlist([]);

  const inCompare = (id) => compare.some((i) => i.id === id);
  const toggleCompare = (product) => {
    setCompare((cur) => {
      if (cur.some((i) => i.id === product.id)) return cur.filter((i) => i.id !== product.id);
      if (cur.length >= 3) return cur; // max 3
      return [...cur, _mini(product)];
    });
  };
  const removeFromCompare = (id) => setCompare((cur) => cur.filter((i) => i.id !== id));
  const clearCompare = () => setCompare([]);

  return (
    <Ctx.Provider value={{
      user, ready, login, signup, loginWithFirebase, updateProfile, logout,
      selection, addToSelection, updateSelectionQty, updateSelectionDate, removeFromSelection, clearSelection,
      quickAdd, openQuickAdd, closeQuickAdd,
      wishlist, isWishlisted, toggleWishlist, clearWishlist,
      compare, inCompare, toggleCompare, removeFromCompare, clearCompare,
      serviceSelection, inServiceSelection, toggleServiceSelection, clearServiceSelection,
    }}>
      {children}
    </Ctx.Provider>
  );
};

export const useApp = () => useContext(Ctx);
